import React, { useState, useEffect, useContext, Suspense } from "react";
import "./App.css";
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { authFetch, tokenManager } from './utils/authFetch';
import { PdfProvider } from './context/PdfContext';
import Footer from './components/Footer/Footer';

// Lazy load components for better performance
const DocumentPage = React.lazy(() => import('./pages/DocumentPage/DocumentPage'));
const Navbar = React.lazy(() => import('./components/Navbar/Navbar'));
const DiscussionPage = React.lazy(() => import('./pages/DiscussionPage/DiscussionPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage/ProfilePage'));
const TopicFormsPage = React.lazy(() => import('./pages/TopicFormsPage/TopicFormsPage'));
const FormDetailPage = React.lazy(() => import('./pages/FormDetailPage/FormDetailPage'));
const QuestionHistoryPage = React.lazy(() => import('./pages/QuestionHistoryPage/QuestionHistoryPage'));
const UploadPage = React.lazy(() => import('./pages/UploadPage/UploadPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const LoginForm = React.lazy(() => import('./components/LoginForm/LoginForm'));
const UserAvatar = React.lazy(() => import('./components/UserAvatar/UserAvatar'));
const HomePage = React.lazy(() => import('./pages/HomePage/HomePage'));

// Theme Context
export const ThemeContext = React.createContext();

// Loading component for Suspense
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '18px',
    color: '#667eea'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #667eea',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginRight: '12px'
    }}></div>
    Đang tải...
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const getToastStyle = () => {
    const baseStyle = {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '16px 20px',
      borderRadius: '8px',
      color: 'white',
      fontWeight: '500',
      zIndex: 1000,
      maxWidth: '400px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      transform: 'translateX(0)',
      transition: 'transform 0.3s ease',
      cursor: 'pointer'
    };

    switch (type) {
      case 'success':
        return { ...baseStyle, backgroundColor: '#10b981' };
      case 'error':
        return { ...baseStyle, backgroundColor: '#ef4444' };
      case 'warning':
        return { ...baseStyle, backgroundColor: '#f59e0b' };
      default:
        return { ...baseStyle, backgroundColor: '#3b82f6' };
    }
  };

  return (
    <div style={getToastStyle()} onClick={onClose}>
      {message}
      <button 
        onClick={onClose}
        style={{
          background: 'none',
          border: 'none',
          color: 'white',
          marginLeft: '12px',
          cursor: 'pointer',
          fontSize: '16px'
        }}
      >
        ×
      </button>
      <div className="toast-progress" />
    </div>
  );
};

function App() {
  const [theme, setTheme] = useState("light");
  const [toast, setToast] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  const showToast = (message, type = "info") => {
    setToast({ message, type });
  };

  const closeToast = () => {
    setToast(null);
  };

  const toggleTheme = () => {
    setTheme(prev => prev === "light" ? "dark" : "light");
  };

  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('accessToken');
      setIsLoggedIn(!!token);
    };

    checkLoginStatus();

    window.addEventListener("storage", (event) => {
      if (event.key === "accessToken") {
        checkLoginStatus();
        }
    });

    return () => {
      window.removeEventListener("storage", checkLoginStatus);
    };
  }, []);

  return (
    <HelmetProvider>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <div className={`app ${theme}`} data-theme={theme}>
          <Suspense fallback={<LoadingSpinner />}>
      <Router>
            <Navbar showToast={showToast} />
            <main className="main-content">
                <PdfProvider>
              <Routes>
                <Route path="/" element={<HomePage showToast={showToast} />} />
                <Route path="/upload" element={<UploadPage showToast={showToast} />} />
                    <Route path="/document" element={<DocumentPage />} />
                <Route path="/discussion" element={<DiscussionPage showToast={showToast} />} />
                <Route path="/discussion/:topicId/forms" element={<TopicFormsPage showToast={showToast} />} />
                    <Route path="/discussion/:topicId/forms/:formId" element={<FormDetailPage />} />
                <Route path="/profile" element={<ProfilePage showToast={showToast} />} />
                    <Route path="/question-history" element={<QuestionHistoryPage />} />
                    <Route path="*" element={<NotFoundPage />} />
              </Routes>
                </PdfProvider>
            </main>

            {/* Footer mới */}
            <Footer />
          </Router>
          </Suspense>

                {toast && (
                  <Toast
                    message={toast.message}
                    type={toast.type}
              onClose={closeToast}
                  />
                )}
            </div>
          </ThemeContext.Provider>
    </HelmetProvider>
  );
}

export default App;
